import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { onlineManager } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/useAuthStore';
import { useGuestStore } from '@/stores/useGuestStore';
import { usePreferenceStore } from '@/stores/usePreferenceStore';
import { tokenExpiry } from '@/utils/tokenExpiry';
import type { CredentialFlow } from '@/networks/types';
import type { AuthSession } from '@/types/auth';
import type { ChatIntent } from '@/types/chat';
import { withChatIntent } from '@/utils/chatIntent';
import {
  SearchChatDraftLease,
  type SearchDraftScope,
  type AdoptedSearchDraft,
} from '@/utils/searchChatDraftLease';
function scope(): SearchDraftScope {
  const auth = useAuthStore.getState(),
    guest = useGuestStore.getState(),
    preference = usePreferenceStore.getState();
  const token = auth.accessToken ?? guest.guestToken;
  return {
    authEpoch: auth.sessionEpoch,
    guestEpoch: guest.sessionEpoch,
    memberId: auth.accessToken ? auth.member?.id : undefined,
    accessToken: auth.accessToken,
    guestToken: guest.guestToken,
    locale: preference.locale ?? 'en',
    currency: preference.currency,
    expiresAt: token ? tokenExpiry(token) : undefined,
    visible: document.visibilityState !== 'hidden',
    online: navigator.onLine !== false && onlineManager.isOnline(),
  };
}
interface DraftContext {
  info?: { id: string; deadline: number };
  start: (text: string) => string | undefined;
  link: (path: string) => string;
  chooseRoom: (roomId: string) => boolean;
  take: (roomId: string) => AdoptedSearchDraft | undefined;
  clear: () => void;
  credentials: (
    redirect: string,
    flow: CredentialFlow,
    session: AuthSession,
    commit: () => void,
  ) => void;
}
const Context = createContext<DraftContext>({
  start: () => undefined,
  link: (path) => path,
  chooseRoom: () => false,
  take: () => undefined,
  clear: () => {},
  credentials: (_redirect, _flow, _session, commit) => commit(),
});
export const useSearchConversationDraft = () => useContext(Context);
export function useSearchDraftPath() {
  const draft = useSearchConversationDraft();
  return (path: string, intent?: ChatIntent) =>
    draft.link(withChatIntent(path, intent));
}
export function SearchConversationDraftProvider({
  children,
}: {
  children: ReactNode;
}) {
  const location = useLocation(),
    path = location.pathname + location.search;
  const [lease] = useState(() => new SearchChatDraftLease());
  const [info, setInfo] = useState<DraftContext['info']>();
  const synchronize = useCallback(
    () => setInfo(lease.info(scope(), path)),
    [lease, path],
  );
  const clear = useCallback(() => {
    lease.clear();
    setInfo(undefined);
  }, [lease]);
  useEffect(() => {
    let active = true;
    queueMicrotask(() => {
      if (active) synchronize();
    });
    const unsubscribe = [
      useAuthStore.subscribe(synchronize),
      useGuestStore.subscribe(synchronize),
      usePreferenceStore.subscribe(synchronize),
      onlineManager.subscribe((online) => {
        if (!online) clear();
      }),
    ];
    const hidden = () => {
      if (document.visibilityState === 'hidden') clear();
    };
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('pagehide', clear);
    window.addEventListener('offline', clear);
    return () => {
      active = false;
      unsubscribe.forEach((stop) => stop());
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('pagehide', clear);
      window.removeEventListener('offline', clear);
    };
  }, [synchronize, clear]);
  const deadline = info?.deadline;
  useEffect(() => {
    if (deadline === undefined) return;
    const timer = setTimeout(clear, Math.max(0, deadline - performance.now()));
    return () => clearTimeout(timer);
  }, [deadline, clear]);
  useEffect(() => () => lease.clear(), [lease]);
  const value: DraftContext = {
    info,
    start: (text) => lease.start(text, scope()),
    link: (target) => lease.link(target, scope(), path),
    chooseRoom: (roomId) => lease.chooseRoom(roomId, scope(), path),
    take: (roomId) => {
      const result = lease.take(roomId, scope(), path);
      setInfo(undefined);
      return result;
    },
    clear,
    credentials: (redirect, flow, session, commit) => {
      lease.credentials(redirect, flow, session, commit, scope(), path, scope);
      synchronize();
    },
  };
  return <Context value={value}>{children}</Context>;
}
